import { useState } from "react";
import type { LineModifier, ModifierGroup, Product } from "@shared/events";
import type { CurrencyCode } from "@shared/money";
import { formatMinor } from "@shared/money";
import { initialSelection, selectionError, selectionToModifiers, toggleModifier, unitPriceMinor, type Selection } from "./cart";

/**
 * Full-screen modifier sheet (F-304): large toggle buttons, min/max enforced, one Add button.
 * Products without modifier groups never reach this screen — they add instantly.
 */
function groupRule(g: ModifierGroup): string {
  if (g.minSelect === g.maxSelect) return g.minSelect === 1 ? "Choose 1" : `Choose ${g.minSelect}`;
  if (g.minSelect === 0) return g.maxSelect === 1 ? "Optional, choose 1" : `Optional, up to ${g.maxSelect}`;
  return `Choose ${g.minSelect}–${g.maxSelect}`;
}

function delta(amount: number, currency: CurrencyCode): string {
  if (amount === 0) return "";
  const sign = amount > 0 ? "+" : "−";
  return `${sign}${formatMinor(Math.abs(amount), currency)}`;
}

export function ModifierSheet(props: {
  product: Product;
  currency: CurrencyCode;
  onAdd: (modifiers: LineModifier[]) => void;
  onCancel: () => void;
}) {
  const groups = [...props.product.modifierGroups].sort((a, b) => a.sortOrder - b.sortOrder);
  const [selection, setSelection] = useState<Selection>(() => initialSelection(groups));
  const error = selectionError(groups, selection);
  const modifiers = selectionToModifiers(groups, selection);
  const price = unitPriceMinor(props.product, modifiers);

  return (
    <section className="sell__sheet" role="dialog" aria-modal="true" aria-label={`Options for ${props.product.label}`}>
      <header className="sell__sheet-head">
        <h2 className="sell__sheet-title">{props.product.label}</h2>
        <button type="button" className="sell__btn sell__btn--quiet sell__sheet-close" onClick={props.onCancel}>
          Cancel
        </button>
      </header>

      <div className="sell__sheet-body">
        {groups.map((g) => {
          const chosen = selection[g.id] ?? [];
          return (
            <fieldset key={g.id} className="sell__group" style={{ border: 0, margin: 0, padding: 0 }}>
              <div className="sell__group-head">
                <legend className="sell__group-name" style={{ padding: 0 }}>
                  {g.name}
                </legend>
                <span className="sell__group-rule">{groupRule(g)}</span>
              </div>
              <div className="sell__options">
                {[...g.modifiers]
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((m) => {
                    const on = chosen.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        className="sell__option"
                        aria-pressed={on}
                        onClick={() => setSelection((s) => toggleModifier(s, g, m.id))}
                      >
                        <span>{m.label}</span>
                        {m.priceDeltaMinor !== 0 ? (
                          <span className="sell__option-delta">{delta(m.priceDeltaMinor, props.currency)}</span>
                        ) : null}
                      </button>
                    );
                  })}
              </div>
            </fieldset>
          );
        })}
      </div>

      <footer className="sell__sheet-foot">
        {error ? (
          <p className="sell__error" role="status">
            {error}
          </p>
        ) : null}
        <button
          type="button"
          className="sell__btn sell__btn--block"
          disabled={error !== null}
          onClick={() => props.onAdd(modifiers)}
        >
          Add · {formatMinor(price, props.currency)}
        </button>
      </footer>
    </section>
  );
}
